// Typed client for the Product Control Plane BFF.
//
// Every call goes through `apiCall`, so it inherits the session bearer, the
// `credentials: "omit"` policy, the 401 handling and the RouteNotFoundError
// behaviour the rest of this SPA already relies on. Nothing here reaches D1,
// signs anything, or holds a credential.

import { apiCall } from "../api/client";
import type { ControlPlaneDocument, Lane } from "./contract";

const BASE = "/v2/admin/bff/control-plane";

export interface ProductRow {
  productId: string;
  displayName: string;
  enabled: boolean;
  currentRevisionId: string | null;
  pointerSeq: number | null;
  publishedAt: string | null;
  publishedBy: string | null;
  documentHash: string | null;
  hasUnpublishedDraft: boolean;
}

export interface RegistryResponse {
  ok: true;
  lane: Lane;
  issuer: string;
  laneRecognized: boolean;
  products: ProductRow[];
  requestId: string;
}

export interface PublishedView {
  revisionId: string;
  revisionSeq: number;
  schemaVersion: number;
  documentHash: string;
  publishedAt: string;
  publishedBy: string;
  publishedByLabel: string | null;
  document: ControlPlaneDocument;
}

export interface DraftMeta {
  documentHash: string;
  baseRevisionId: string | null;
  updatedAt: string;
  updatedBy: string;
  derivedFromPublished: boolean;
  matchesPublished: boolean;
}

export interface ProductDetailResponse {
  ok: true;
  lane: Lane;
  issuer: string;
  laneRecognized: boolean;
  product: { productId: string; displayName: string; enabled: boolean; createdAt: string };
  current: {
    revisionId: string;
    pointerSeq: number;
    updatedAt: string;
    updatedBy: string;
  } | null;
  published: PublishedView | null;
  publishedError: string | null;
  draft: DraftMeta | null;
  requestId: string;
}

export interface DraftResponse extends DraftMeta {
  ok: true;
  document: ControlPlaneDocument;
  requestId: string;
}

export interface DocumentIssue {
  path: string;
  code: string;
  message: string;
  itemScoped: boolean;
}

export interface ValidationReport {
  ok: true;
  valid: boolean;
  issues: DocumentIssue[];
  documentHash: string | null;
  byteLength: number | null;
  requestId: string;
}

export interface RevisionSummary {
  revisionId: string;
  revisionSeq: number;
  schemaVersion: number;
  documentHash: string;
  publishedAt: string;
  publishedBy: string;
  publishedByLabel: string | null;
  isCurrent: boolean;
}

export interface RevisionsResponse {
  ok: true;
  currentRevisionId: string | null;
  limit: number;
  revisions: RevisionSummary[];
  requestId: string;
}

export interface RevisionResponse extends Omit<RevisionSummary, "isCurrent"> {
  ok: true;
  isCurrent: boolean;
  document: ControlPlaneDocument;
  requestId: string;
}

export interface AuditEvent {
  id: number;
  occurredAt: string;
  actor: string;
  actorLabel: string | null;
  action:
    | "DRAFT_CREATED"
    | "DRAFT_UPDATED"
    | "DRAFT_RESET"
    | "VALIDATED"
    | "PUBLISHED"
    | "ROLLED_BACK";
  productId: string;
  surfaceId: string | null;
  fromRevision: string | null;
  toRevision: string | null;
  documentHash: string | null;
  result: "ok" | "rejected" | "error";
  detail: string | null;
  requestId: string | null;
}

export interface AuditResponse {
  ok: true;
  limit: number;
  events: AuditEvent[];
  requestId: string;
}

export interface PreviewResponse {
  ok: true;
  source: "published" | "draft";
  lane: Lane;
  appVersion: string | null;
  appVersionDeclared: string | null;
  revisionId?: string;
  document: ControlPlaneDocument;
  requestId: string;
}

export interface PublishResponse {
  ok: true;
  revisionId: string;
  revisionSeq: number;
  documentHash: string;
  publishedAt: string;
  previousRevisionId: string | null;
  requestId: string;
}

export interface RollbackResponse {
  ok: true;
  fromRevisionId: string;
  toRevisionId: string;
  documentHash: string;
  requestId: string;
}

export interface BootstrapResponse {
  ok: true;
  revisionId: string;
  documentHash: string;
  publishedAt: string;
  migrated: {
    sourceRowCount: number;
    carriedCount: number;
    dropped: Array<{ id: string; reason: string }>;
  };
  requestId: string;
}

// ── Reads ───────────────────────────────────────────────────────────────────

export function listProducts(): Promise<RegistryResponse> {
  return apiCall<RegistryResponse>(`${BASE}/products`);
}

export function getProduct(productId: string): Promise<ProductDetailResponse> {
  return apiCall<ProductDetailResponse>(`${BASE}/products/${encodeURIComponent(productId)}`);
}

export function getDraft(productId: string): Promise<DraftResponse> {
  return apiCall<DraftResponse>(`${BASE}/products/${encodeURIComponent(productId)}/draft`);
}

export function listRevisions(productId: string): Promise<RevisionsResponse> {
  return apiCall<RevisionsResponse>(`${BASE}/products/${encodeURIComponent(productId)}/revisions`);
}

export function getRevision(
  productId: string,
  revisionId: string,
): Promise<RevisionResponse> {
  return apiCall<RevisionResponse>(
    `${BASE}/products/${encodeURIComponent(productId)}/revisions/${encodeURIComponent(revisionId)}`,
  );
}

export function listAudit(productId: string): Promise<AuditResponse> {
  return apiCall<AuditResponse>(`${BASE}/products/${encodeURIComponent(productId)}/audit`);
}

/** What a consumer on `lane` declaring `appVersion` would actually receive.
 *  The lane may be chosen here — and ONLY here — because this is a preview
 *  behind the owner gate, never a consumer response. */
export function preview(
  productId: string,
  opts: { lane?: Lane; appVersion?: string; source?: "published" | "draft" } = {},
): Promise<PreviewResponse> {
  return apiCall<PreviewResponse>(
    `${BASE}/products/${encodeURIComponent(productId)}/preview`,
    {
      query: {
        lane: opts.lane,
        appVersion: opts.appVersion,
        source: opts.source,
      },
    },
  );
}

// ── Writes ──────────────────────────────────────────────────────────────────

export function saveDraft(
  productId: string,
  document: ControlPlaneDocument,
): Promise<{ ok: true; documentHash: string; issues: DocumentIssue[]; requestId: string }> {
  return apiCall(`${BASE}/products/${encodeURIComponent(productId)}/draft`, {
    method: "PUT",
    body: { document },
  });
}

export function resetDraft(
  productId: string,
): Promise<{ ok: true; documentHash: string; resetToRevisionId: string; requestId: string }> {
  return apiCall(`${BASE}/products/${encodeURIComponent(productId)}/draft/reset`, {
    method: "POST",
  });
}

export function validateDraft(
  productId: string,
  document?: ControlPlaneDocument,
): Promise<ValidationReport> {
  return apiCall<ValidationReport>(
    `${BASE}/products/${encodeURIComponent(productId)}/validate`,
    { method: "POST", body: document ? { document } : undefined },
  );
}

/** Mint revision 1 from the legacy `explorer_sites` table. Idempotent by
 *  refusal — 409 once any revision exists. */
export function bootstrap(productId: string): Promise<BootstrapResponse> {
  return apiCall<BootstrapResponse>(
    `${BASE}/products/${encodeURIComponent(productId)}/bootstrap`,
    { method: "POST" },
  );
}

/**
 * Publish the draft.
 *
 * `expectedBaseRevisionId` is optimistic concurrency and is always sent by this
 * console: if someone else published between load and publish, the server
 * refuses with 409 rather than burying their revision under this operator's
 * older draft.
 */
export function publish(
  productId: string,
  expectedBaseRevisionId: string | null,
): Promise<PublishResponse> {
  return apiCall<PublishResponse>(
    `${BASE}/products/${encodeURIComponent(productId)}/publish`,
    {
      method: "POST",
      body:
        expectedBaseRevisionId !== null
          ? { expectedBaseRevisionId }
          : {},
    },
  );
}

export function rollback(
  productId: string,
  revisionId: string,
): Promise<RollbackResponse> {
  return apiCall<RollbackResponse>(
    `${BASE}/products/${encodeURIComponent(productId)}/rollback`,
    { method: "POST", body: { revisionId } },
  );
}
